// Small shared pieces for the End of day screens (hub, check-in, team, numbers).
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';
import { addDaysIso, initials, shortDay, type FlagTone } from '@/src/lib/checkin';

export function useTone() {
  const c = useAppColors();
  const d = c.isDark;
  return (t: FlagTone) => ({
    red: { color: d ? '#FCA5A5' : '#9A2A1C', background: d ? 'rgba(248,113,113,0.14)' : '#FCE8E4', borderColor: d ? 'rgba(248,113,113,0.45)' : '#E7B3AA' },
    amber: { color: d ? '#FCD38D' : '#8A4B08', background: d ? 'rgba(232,160,32,0.16)' : '#FCEFD9', borderColor: d ? 'rgba(232,160,32,0.45)' : '#EBC992' },
    blue: { color: d ? '#9CC7F0' : '#1F4E79', background: d ? 'rgba(96,165,250,0.15)' : '#E3EEF8', borderColor: d ? 'rgba(96,165,250,0.45)' : '#AFC9E3' },
    green: { color: c.accentSoft, background: c.accentBg, borderColor: c.accentBorder },
    grey: { color: c.text, background: c.iconBg, borderColor: c.borderStrong },
  }[t]);
}

export function Chip({ tone, children }: { tone: FlagTone; children: ReactNode }) {
  const t = useTone()(tone);
  return <span className="inline-flex items-center text-[12px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ color: t.color, background: t.background }}>{children}</span>;
}

export function Avatar({ name, size = 36, pending }: { name: string; size?: number; pending?: boolean }) {
  const c = useAppColors();
  return (
    <span className="rounded-full flex items-center justify-center font-bold shrink-0 box-border"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.32),
        ...(pending ? { border: `2px dashed ${c.borderStrong}`, color: c.textDim, background: 'transparent' } : { background: c.accent, color: '#fff' }) }}
      aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export function DateStepper({ date, max, onChange, label }: { date: string; max: string; onChange: (d: string) => void; label?: string }) {
  const c = useAppColors();
  const rel = date === max ? 'Tonight' : date === addDaysIso(max, -1) ? 'Last night' : null;
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => onChange(addDaysIso(date, -1))} aria-label="Previous day"
        className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
        <ChevronLeft size={17} />
      </button>
      <label className="relative h-10 px-3.5 rounded-xl flex items-center gap-2 text-sm font-semibold whitespace-nowrap cursor-pointer"
        style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
        {label ?? shortDay(date)}
        {rel && !label && <span className="text-xs font-normal" style={{ color: c.textDim }}>{rel}</span>}
        <input type="date" value={date} max={max} onChange={(e) => e.target.value && onChange(e.target.value > max ? max : e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Pick a day" />
      </label>
      <button onClick={() => onChange(addDaysIso(date, 1))} disabled={date >= max} aria-label="Next day"
        className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center disabled:opacity-35" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
        <ChevronRight size={17} />
      </button>
    </div>
  );
}

export function BackButton({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  const c = useAppColors();
  return (
    <button onClick={onClick} aria-label={label}
      className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
      <ChevronLeft size={18} />
    </button>
  );
}

/** Segmented progress bar, e.g. 3 of 7 steps done. Clickable when onPick is given. */
export function StepBar({ total, done, current, onPick, labels }: { total: number; done: boolean[]; current?: number; onPick?: (i: number) => void; labels?: string[] }) {
  const c = useAppColors();
  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }} role={onPick ? undefined : 'img'}
      aria-label={onPick ? undefined : `${done.filter(Boolean).length} of ${total} steps done`}>
      {Array.from({ length: total }, (_, i) => {
        const bg = done[i] ? c.accent : i === current ? c.accentBorder : c.borderStrong;
        return onPick ? (
          <button key={i} onClick={() => onPick(i)} aria-label={labels?.[i] ?? `Step ${i + 1}`} className="h-6 flex items-center">
            <span className="block w-full h-1.5 rounded-full" style={{ background: bg }} />
          </button>
        ) : <span key={i} className="h-1.5 rounded-full" style={{ background: bg }} />;
      })}
    </div>
  );
}

export function Panel({ children, className = '', strong, dashed }: { children: ReactNode; className?: string; strong?: boolean; dashed?: boolean }) {
  const c = useAppColors();
  return (
    <section className={`rounded-2xl ${className}`}
      style={{ background: dashed ? 'transparent' : c.bgElevated, border: strong ? `2px solid ${c.accent}` : `1px ${dashed ? 'dashed' : 'solid'} ${dashed ? c.borderStrong : c.border}` }}>
      {children}
    </section>
  );
}
